import { getDataClient } from "@/lib/dataClient";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";

export interface TeacherLiveClass {
  id: string;
  title: string;
  subject: string;
  grade: string;
  teacher: string;
  teacherAvatar: string;
  date: string;
  time: string;
  duration: number;
  status: "planned" | "live" | "ended" | "cancelled";
  participants: number;
  maxParticipants: number;
  description: string;
  hasRecording: boolean;
  roomCode: string;
  studentIds: string[];
}

function fmtDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return { date: `${y}.${m}.${day}`, time: `${h}:${min}` };
}

function mapRow(lc: {
  id: string;
  subject: string;
  title: string;
  teacher_name: string | null;
  teacher_avatar: string | null;
  grade: string | null;
  starts_at: string;
  duration_minutes: number | null;
  status: string;
  participants: number | null;
  max_participants: number | null;
  description: string | null;
  has_recording: boolean;
  room_code: string | null;
  student_ids: string[] | null;
}): TeacherLiveClass {
  const dt = fmtDateTime(lc.starts_at);
  return {
    id: lc.id,
    title: lc.title,
    subject: lc.subject,
    grade: lc.grade ?? "",
    teacher: lc.teacher_name ?? "",
    teacherAvatar: lc.teacher_avatar ?? "",
    date: dt.date,
    time: dt.time,
    duration: lc.duration_minutes ?? 0,
    status: lc.status as TeacherLiveClass["status"],
    participants: lc.participants ?? 0,
    maxParticipants: lc.max_participants ?? 0,
    description: lc.description ?? "",
    hasRecording: lc.has_recording ?? false,
    roomCode: lc.room_code ?? "",
    studentIds: lc.student_ids ?? [],
  };
}

/**
 * Öğretmenin kendi canlı derslerini çeker (teacher_id = mevcut kullanıcı).
 */
export function useTeacherLiveClasses() {
  const query = useDataQuery<TeacherLiveClass[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("live_classes")
      .select("*")
      .eq("teacher_id", userId)
      .order("starts_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapRow);
  });

  const createLiveClass = async (input: {
    title: string;
    subject: string;
    grade: string;
    starts_at: string;
    duration_minutes: number;
    description: string;
    studentIds: string[];
  }) => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return;

    const { data: authData } = await dataClient.auth.getUser();
    const metaName = authData?.user?.user_metadata?.ad_soyad;
    const teacherName = typeof metaName === "string" && metaName ? metaName : "Öğretmen";
    const roomCode = `DD-${Date.now().toString(36).toUpperCase()}`;

    const { error } = await dataClient.from("live_classes").insert({
      teacher_id: userId,
      title: input.title,
      subject: input.subject,
      grade: input.grade,
      teacher_name: teacherName,
      starts_at: input.starts_at,
      duration_minutes: input.duration_minutes,
      description: input.description,
      status: "planned",
      participants: 0,
      max_participants: 40,
      has_recording: false,
      room_code: roomCode,
      student_ids: input.studentIds,
    });
    if (error) throw error;
    query.reload();
  };

  const startLive = async (id: string) => {
    const dataClient = getDataClient();
    const { error } = await dataClient.from("live_classes").update({ status: "live" }).eq("id", id);
    if (error) throw error;
    query.reload();
  };

  const endLive = async (id: string) => {
    const dataClient = getDataClient();
    const { error } = await dataClient.from("live_classes").update({ status: "ended" }).eq("id", id);
    if (error) throw error;
    query.reload();
  };

  const cancelClass = async (id: string) => {
    const dataClient = getDataClient();
    const { error } = await dataClient.from("live_classes").update({ status: "cancelled" }).eq("id", id);
    if (error) throw error;
    query.reload();
  };

  const updateStudents = async (id: string, studentIds: string[]) => {
    const dataClient = getDataClient();
    const { error } = await dataClient.from("live_classes").update({ student_ids: studentIds }).eq("id", id);
    if (error) throw error;
    query.reload();
  };

  return { ...query, createLiveClass, startLive, endLive, cancelClass, updateStudents };
}
